import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { RecordingPlayer } from '../components/player';
import { Recording } from '../types';
import { RecordingService } from '../lib/firebase/recordingService';
import { useAuthStore } from '../store/useAuthStore';
import { useUserStore } from '../store/useUserStore';
import { SolutionListPanel } from '../components/SolutionListPanel';
// import { AudioDebugger } from '../components/player/AudioDebugger'; // Removed for production

const PlayerPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const userInfo = useUserStore(state => state.userInfo);
  
  // Store previous location for proper navigation
  const previousLocationRef = useRef(location.state?.from || '/');
  
  const [recording, setRecording] = useState<Recording | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requiresAuth, setRequiresAuth] = useState(false);

  // Debug logs - commented out to reduce console noise
  // console.log('PlayerPage rendered with ID:', id);
  // console.log('Auth status:', { isAuthenticated, userInfo });

  useEffect(() => {
    // console.log('PlayerPage useEffect triggered, ID:', id);
    
    if (!id) {
      setError('No recording ID provided');
      setLoading(false);
      return;
    }

    // Load recording from Firebase
    const loadRecording = async () => {
      try {
        const recordingData = await RecordingService.getRecording(id);
        
        if (!recordingData) {
          setError('Recording not found');
          setLoading(false);
          return;
        }
        
        
        // Check if recording is public or user has access
        // TODO: isPublic kontrolü şimdilik devre dışı
        const isPublic = true; // recordingData.metadata.solutionMetadata?.isPublic || false;
        const isOwner = isAuthenticated && userInfo && recordingData.metadata.authorMetadata.id === userInfo.id;
        const hasAccess = isPublic || isOwner;
        
        
        // Geçici olarak tüm kayıtlara erişime izin ver
        /*
        if (!hasAccess) {
          if (!isAuthenticated) {
            setError('Bu kayıt özel. Görüntülemek için giriş yapmalısınız.');
            setRequiresAuth(true);
          } else {
            setError('Bu kaydı görüntüleme yetkiniz yok.');
          }
          setLoading(false);
          return;
        }
        */

        // Convert Firebase recording data to Recording format
        // Handle Firebase Timestamp - it might be a Timestamp object or already a date
        const createdAtTimestamp = recordingData.metadata.createdAt;
        let createdAtMillis: number;
        
        if (createdAtTimestamp && typeof createdAtTimestamp === 'object' && 'toDate' in createdAtTimestamp) {
          // It's a Firebase Timestamp
          createdAtMillis = createdAtTimestamp.toDate().getTime();
        } else if (createdAtTimestamp && typeof createdAtTimestamp === 'object' && 'seconds' in createdAtTimestamp) {
          // It's a Firebase Timestamp-like object with seconds
          createdAtMillis = createdAtTimestamp.seconds * 1000;
        } else if (createdAtTimestamp instanceof Date) {
          // It's already a Date object
          createdAtMillis = createdAtTimestamp.getTime();
        } else {
          // Fallback to current time
          createdAtMillis = Date.now();
        }
        
        // Load audio blob from Firebase Storage if exists
        let audioBlob: Blob | undefined;
        if (recordingData.metadata.storageInfo?.audioPath) {
          try {
            const audioUrl = await RecordingService.getRecordingAudioUrl(recordingData.metadata.storageInfo.audioPath);
            const audioResponse = await fetch(audioUrl);
            audioBlob = await audioResponse.blob();
          } catch (error) {
          }
        }

      

        // Mark all stroke events as normalized since we save normalized events
        const processedEvents = recordingData.actionData.actions.map((event: any) => {
          if (event.type === 'add_stroke' && event.data?.stroke) {
            // Mark stroke as normalized
            return {
              ...event,
              data: {
                ...event.data,
                stroke: {
                  ...event.data.stroke,
                  _isNormalized: true
                }
              }
            };
          }
          return event;
        });


        const recording: Recording = {
          id: recordingData.metadata.id,
          name: recordingData.metadata.questionMetadata?.title || 'Unnamed Recording',
          duration: recordingData.metadata.duration || 0,
          audioBlob: audioBlob,
          audioPath: recordingData.metadata.storageInfo?.audioPath,
          events: processedEvents,
          createdAt: createdAtMillis,
          canvasSize: recordingData.metadata.whiteboardSize,
          questionId: recordingData.metadata.questionMetadata?.id,
          testId: recordingData.metadata.questionMetadata?.testId,
          questionImageUrl: recordingData.metadata.questionMetadata?.imageUrl,
          parentImageUrl: recordingData.metadata.questionMetadata?.parentImageUrl,
          questionMetadata: recordingData.metadata.questionMetadata, // Pass the entire questionMetadata including position
          tenantId: recordingData.metadata.tenantMetadata?.id || recordingData.metadata.tenantId
        };

        setRecording(recording);
        setLoading(false);
      } catch (error) {
        setError('Failed to load recording');
        setLoading(false);
      }
    };

    loadRecording();
  }, [id]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading recording...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <div className="space-x-4">
            {requiresAuth && (
              <button
                onClick={() => navigate('/login')}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                Giriş Yap
              </button>
            )}
            <button
              onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate(previousLocationRef.current);
                }
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              Geri Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!recording) {
    return null;
  }

  return (
    <div className="fixed inset-0 overflow-hidden">
      <RecordingPlayer
        recording={recording}
        onClose={() => {
          // Try to go back in history first
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            // If no history, go to previous location or home
            navigate(previousLocationRef.current);
          }
        }}
      />
      
      {/* Audio Debugger removed for production */}
      
      {/* Solutions Panel - SolutionListPanel handles its own toggle button */}
      {recording?.testId && recording?.questionId && (
        <SolutionListPanel
          testId={recording.testId}
          questionId={recording.questionId}
          currentRecordingId={recording.id}
          tenantId={recording.tenantId}
        />
      )}
    </div>
  );
};

export default PlayerPage;